/** Data-fetching hook for paginated recent AI calls.
 *
 * Fetches the /api/v1/metrics/ai-usage/recent-calls endpoint with
 * server-side pagination. Page state is managed internally; the caller
 * can read and control it via the returned fields.
 */

import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "@/shared/lib/apiClient";
import { queryKeys } from "@/shared/lib/queryKeys";

import type { AIUsageRecentCallsResponse, MetricsRange } from "../lib/metricsTypes";
import { rangeToKey, rangeToQueryParams } from "../lib/metricsTypes";

const DEFAULT_PAGE_SIZE = 25;

interface UseAIRecentCallsResult {
  data: AIUsageRecentCallsResponse | null;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  totalPages: number;
  total: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  refetch: () => void;
}

export function useAIRecentCalls(
  range: MetricsRange,
  initialPage: number = 1,
  initialPageSize: number = DEFAULT_PAGE_SIZE,
): UseAIRecentCallsResult {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const queryClient = useQueryClient();
  const rangeKey = rangeToKey(range);
  const rangeQp = rangeToQueryParams(range);

  const { data, isLoading, error } = useQuery<AIUsageRecentCallsResponse>({
    queryKey: queryKeys.aiUsageRecentCalls(rangeKey, page, pageSize),
    queryFn: () =>
      apiRequest<AIUsageRecentCallsResponse>(
        "GET",
        `/api/v1/metrics/ai-usage/recent-calls?${rangeQp}&page=${page}&page_size=${pageSize}`,
      ),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["metrics", "ai-usage", "recent-calls"] });
  }, [queryClient]);

  const totalPages = data?.total_pages ?? 0;
  const total = data?.total ?? 0;

  const nextPage = useCallback(() => {
    setPage((p) => Math.min(p + 1, totalPages));
  }, [totalPages]);

  const prevPage = useCallback(() => {
    setPage((p) => Math.max(p - 1, 1));
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1);
  }, []);

  return {
    data: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    page,
    pageSize,
    totalPages,
    total,
    setPage,
    setPageSize,
    nextPage,
    prevPage,
    refetch,
  };
}
